import { useState } from 'react';
import { api, money } from '../api';
import { useApi } from '../useApi';
import { Empty, ErrorBox, Loading, useToast } from '../ui';

type Product = {
  id: string;
  sku: string;
  name: string;
  kind: string;
  priceKop: string;
  bottleDelta: number;
  category: string | null;
  vendor: string | null;
  imageUrl: string | null;
  active: boolean;
};

const KIND_RU: Record<string, string> = {
  WATER_19L: 'Вода 19 л (оборотная тара)',
  WATER_BOTTLED: 'Бутилированная вода',
  COOLER: 'Кулеры и диспенсеры',
  PUMP: 'Помпы',
  ACCESSORY: 'Сопутствующие товары',
  SERVICE: 'Услуги',
  RENT: 'Аренда',
  DEPOSIT: 'Залог',
};

/**
 * Каталог. Загружен с сайта компании (767700.ru) вместе с ценами и фото:
 * цена в CRM и цена на сайте расходиться не должны — это первое, что заметит
 * клиент.
 *
 * Правка цены здесь меняет только новые заказы. У выписанного заказа цена уже
 * своя, скопированная в позицию: заказ, который клиент подтвердил по телефону,
 * не должен подорожать задним числом.
 */
export function Catalog() {
  const { data, error, loading, reload } = useApi<Product[]>('/products?all=true');
  const [kind, setKind] = useState<string>('');
  const [edit, setEdit] = useState<Product | null>(null);
  const [adding, setAdding] = useState(false);
  const toast = useToast();

  if (loading) return <Loading />;
  if (error) return <ErrorBox error={error} />;
  if (!data) return null;

  const kinds = [...new Set(data.map((p) => p.kind))];
  const shown = kind ? data.filter((p) => p.kind === kind) : data;

  const done = async (msg: string) => {
    setEdit(null);
    setAdding(false);
    toast.show(msg);
    await reload();
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Товары — {data.length}</h1>
          <div className="muted">Источник: 767700.ru</div>
        </div>
        <button
          className="primary"
          onClick={() => {
            setEdit(null);
            setAdding(!adding);
          }}
        >
          + Товар
        </button>
      </div>

      {adding ? <ProductForm onSaved={() => void done('Товар заведён')} onClose={() => setAdding(false)} /> : null}
      {edit ? (
        <ProductForm
          product={edit}
          onSaved={() => void done('Товар сохранён')}
          onClose={() => setEdit(null)}
        />
      ) : null}

      <div className="tabs">
        <button className={!kind ? 'active' : ''} onClick={() => setKind('')}>
          Все
        </button>
        {kinds.map((k) => (
          <button key={k} className={kind === k ? 'active' : ''} onClick={() => setKind(k)}>
            {KIND_RU[k] ?? k}
          </button>
        ))}
      </div>

      <div className="card">
        {!shown.length ? (
          <Empty>Пусто</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 60 }} />
                <th>Название</th>
                <th>Тип</th>
                <th className="num">Цена</th>
                <th className="num">Тара</th>
                <th>Состояние</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((p) => (
                <tr key={p.id}>
                  <td>
                    {p.imageUrl ? (
                      <img src={p.imageUrl} alt="" style={{ width: 44, height: 44, objectFit: 'contain' }} />
                    ) : null}
                  </td>
                  <td>
                    {p.name}
                    <div className="muted mono" style={{ fontSize: 11 }}>{p.sku}</div>
                  </td>
                  <td className="muted">{KIND_RU[p.kind] ?? p.kind}</td>
                  <td className="num">{BigInt(p.priceKop) > 0n ? money(p.priceKop) : '— по запросу'}</td>
                  <td className="num">{p.bottleDelta ? `+${p.bottleDelta}` : '—'}</td>
                  <td>
                    {p.active ? (
                      <span className="badge green">в продаже</span>
                    ) : (
                      <span className="badge amber">снят</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="ghost"
                      data-edit={p.sku}
                      onClick={() => {
                        setAdding(false);
                        setEdit(p);
                      }}
                    >
                      изменить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        Тара двигается только у воды 19 л: колонка «Тара» показывает, сколько
        бутылей добавится клиенту на руки за единицу товара.
      </div>
      {toast.node}
    </>
  );
}

/**
 * Одна форма и на заведение, и на правку. Поля, которые нельзя трогать после
 * создания — артикул, тип и движение тары, — при правке не показываются:
 * товар, по которому уже есть заказы, не должен на ходу сменить смысл.
 */
function ProductForm({
  product,
  onSaved,
  onClose,
}: {
  product?: Product;
  onSaved: () => void;
  onClose: () => void;
}) {
  const editing = !!product;
  const [form, setForm] = useState({
    sku: product?.sku ?? '',
    name: product?.name ?? '',
    kind: product?.kind ?? 'ACCESSORY',
    rub: product ? (Number(product.priceKop) / 100).toFixed(2) : '',
    bottleDelta: product?.bottleDelta ?? 0,
    active: product?.active ?? true,
  });
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const priceKop = String(Math.round(Number(form.rub.replace(',', '.')) * 100) || 0);

  return (
    <form
      className="card"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          if (editing) {
            await api(`/products/${product.id}`, {
              method: 'PATCH',
              body: { name: form.name, priceKop, active: form.active },
            });
          } else {
            await api('/products', {
              body: {
                sku: form.sku,
                name: form.name,
                kind: form.kind,
                priceKop,
                bottleDelta: form.kind === 'WATER_19L' ? form.bottleDelta || 1 : 0,
              },
            });
          }
          onSaved();
        } catch (e) {
          setError(e);
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="spread">
        <h2>{editing ? `Правка: ${product.name}` : 'Новый товар'}</h2>
        <button type="button" className="ghost" onClick={onClose}>
          Отмена
        </button>
      </div>
      <div className="row">
        {!editing ? (
          <div className="field" style={{ flex: '0 0 160px' }}>
            <label>Артикул</label>
            <input name="sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} required />
          </div>
        ) : null}
        <div className="field" style={{ flex: '2 1 260px' }}>
          <label>Название</label>
          <input name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        {!editing ? (
          <div className="field" style={{ flex: '1 1 200px' }}>
            <label>Тип</label>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
              {Object.entries(KIND_RU).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <div className="field" style={{ flex: '0 0 140px' }}>
          <label>Цена, ₽</label>
          <input name="rub" inputMode="decimal" value={form.rub} onChange={(e) => setForm({ ...form, rub: e.target.value })} required />
        </div>
        {editing ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-end', margin: '0 0 14px' }}>
            <input
              type="checkbox"
              name="active"
              style={{ width: 16 }}
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            в продаже
          </label>
        ) : null}
      </div>
      {!editing && form.kind === 'WATER_19L' ? (
        <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
          Вода 19 л — оборотная тара: каждая единица добавит клиенту бутыль на руки.
        </div>
      ) : null}
      <ErrorBox error={error} />
      <button className="primary" disabled={busy}>
        {editing ? 'Сохранить' : 'Завести товар'}
      </button>
    </form>
  );
}
